import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function MolecularFormulaCalculator() {
  const [empiricalFormula, setEmpiricalFormula] = useState("");
  const [empiricalMass, setEmpiricalMass] = useState("");
  const [molarMass, setMolarMass] = useState("");
  const [result, setResult] = useState<string | null>(null);

  const calculate = () => {
    const em = parseFloat(empiricalMass);
    const mm = parseFloat(molarMass);

    if (!empiricalFormula || isNaN(em) || isNaN(mm) || em <= 0 || mm <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter all required values.",
        variant: "destructive",
      });
      return;
    }

    const multiplier = Math.round(mm / em);
    
    setResult(`(${empiricalFormula})₍${multiplier}₎`);

    toast({
      title: "Calculation Complete",
      description: `Molecular Formula: Multiple of ${multiplier}`,
    });
  };

  const reset = () => {
    setEmpiricalFormula("");
    setEmpiricalMass("");
    setMolarMass("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Molecular Formula Calculator" 
      calculatorPath="/calculator/molecular-formula"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Molecular Formula Calculator</CardTitle>
            <CardDescription>
              Calculate molecular formula from empirical formula
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="empiricalFormula">Empirical Formula</Label>
                <Input
                  id="empiricalFormula"
                  value={empiricalFormula}
                  onChange={(e) => setEmpiricalFormula(e.target.value)}
                  placeholder="e.g., CH2O"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="empiricalMass">Empirical Formula Mass (g/mol)</Label>
                <Input
                  id="empiricalMass"
                  type="number"
                  step="any"
                  value={empiricalMass}
                  onChange={(e) => setEmpiricalMass(e.target.value)}
                  placeholder="Enter empirical mass"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="molarMass">Molar Mass (g/mol)</Label>
                <Input
                  id="molarMass"
                  type="number"
                  step="any"
                  value={molarMass}
                  onChange={(e) => setMolarMass(e.target.value)}
                  placeholder="Enter molar mass"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Molecular Formula</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{result}</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">n = Molar Mass / Empirical Mass</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Molecular Formula = (Empirical Formula)ₙ
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
